import { ChangeEvent } from "react";
import { Box, MenuItem, FormControlLabel, IconButton, Switch, TextField, Tooltip, Typography } from "@mui/material";
import RestoreIcon from "@mui/icons-material/Restore";
import { useFormikContext } from "formik";
import type { RuntimeConfig } from "../pages/ConfigPage";

type MosaicDefaults = Pick<
  RuntimeConfig,
  | "previewAcceleration"
  | "enablePreview"
  | "previewMemoryBudgetMiB"
  | "previewWorkers"
  | "recastPreviewIntervalMs"
  | "previewFramerate"
  | "previewWidth"
  | "previewHeight"
  | "previewCaptureTimeoutMs"
  | "previewTargetWarmupGraceMs"
  | "previewBackoffMinMs"
  | "previewBackoffMaxMs"
>;

type MosaicFormProps = {
  defaults: MosaicDefaults;
};

const PreviewCfgForm = ({ defaults }: MosaicFormProps) => {
  const formik = useFormikContext<RuntimeConfig>();
  const fieldWidth = 220;
  const previewEnabled = formik.values.enablePreview;

  const getError = (field: keyof Pick<
    RuntimeConfig,
    | "previewMemoryBudgetMiB"
    | "previewWorkers"
    | "recastPreviewIntervalMs"
    | "previewFramerate"
    | "previewWidth"
    | "previewHeight"
    | "previewCaptureTimeoutMs"
    | "previewTargetWarmupGraceMs"
    | "previewBackoffMinMs"
    | "previewBackoffMaxMs"
  >) => {
    const touched = formik.touched[field];
    if (!touched && formik.submitCount === 0) {
      return undefined;
    }
    return formik.errors[field] as string | undefined;
  };

  const formatNumberValue = (value: number) => (value === 0 ? "" : value);

  const handleIntegerChange = (
    field: "previewMemoryBudgetMiB" | "previewWorkers"
      | "recastPreviewIntervalMs"
      | "previewWidth"
      | "previewHeight"
      | "previewCaptureTimeoutMs"
      | "previewTargetWarmupGraceMs"
      | "previewBackoffMinMs"
      | "previewBackoffMaxMs"
  ) => (event: ChangeEvent<HTMLInputElement>) => {
    const parsed = Number.parseInt(event.target.value, 10);
    void formik.setFieldValue(field, Number.isNaN(parsed) ? 0 : parsed);
  };

  const handleFloatChange = (field: "previewFramerate") => (event: ChangeEvent<HTMLInputElement>) => {
    const parsed = Number.parseFloat(event.target.value);
    void formik.setFieldValue(field, Number.isNaN(parsed) ? 0 : parsed);
  };

  const isDefaultValues =
    formik.values.previewAcceleration === defaults.previewAcceleration &&
    formik.values.enablePreview === defaults.enablePreview &&
    formik.values.previewMemoryBudgetMiB === defaults.previewMemoryBudgetMiB &&
    formik.values.previewWorkers === defaults.previewWorkers &&
    formik.values.recastPreviewIntervalMs === defaults.recastPreviewIntervalMs &&
    formik.values.previewFramerate === defaults.previewFramerate &&
    formik.values.previewWidth === defaults.previewWidth &&
    formik.values.previewHeight === defaults.previewHeight &&
    formik.values.previewCaptureTimeoutMs === defaults.previewCaptureTimeoutMs &&
    formik.values.previewTargetWarmupGraceMs === defaults.previewTargetWarmupGraceMs &&
    formik.values.previewBackoffMinMs === defaults.previewBackoffMinMs &&
    formik.values.previewBackoffMaxMs === defaults.previewBackoffMaxMs;

  const handleRestoreDefaults = () => {
    void formik.setValues({
      ...formik.values,
      ...defaults
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Stream preview settings
        </Typography>
        <Tooltip title="Restore defaults">
          <span>
            <IconButton
              size="small"
              color="primary"
              aria-label="Restore defaults"
              onClick={handleRestoreDefaults}
              disabled={isDefaultValues}
            >
              <RestoreIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <TextField select label="Preview acceleration" name="previewAcceleration"
        disabled={!previewEnabled} value={formik.values.previewAcceleration} onChange={formik.handleChange}
        helperText="Auto prefers supported hardware and falls back to software. Adaptive helpers update independently; legacy decoders update on the next stream start."
        sx={{ maxWidth: 560 }}>
        <MenuItem value="auto">Auto</MenuItem>
        <MenuItem value="software">Software</MenuItem>
      </TextField>
      <TextField
        label="Preview RAM budget (MiB)"
        name="previewMemoryBudgetMiB"
        type="number"
        disabled={!previewEnabled}
        value={formatNumberValue(formik.values.previewMemoryBudgetMiB)}
        onChange={handleIntegerChange("previewMemoryBudgetMiB")}
        onBlur={formik.handleBlur}
        error={Boolean(getError("previewMemoryBudgetMiB"))}
        helperText={getError("previewMemoryBudgetMiB") || "Adaptive previews: supervised budget; brief overshoot is possible. Zero uses 512 MiB."}
        inputProps={{ min: 0, step: 1 }}
        sx={{ maxWidth: 460 }}
      />
      <Tooltip title="Enable or disable stream preview extraction and Mosaic previews.">
        <FormControlLabel
          control={(
            <Switch
              size="small"
              name="enablePreview"
              checked={formik.values.enablePreview}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          )}
          label="Enable Preview"
          sx={{ alignSelf: "flex-start" }}
        />
      </Tooltip>
      <TextField label="Recast-only preview interval (ms)" name="recastPreviewIntervalMs"
        type="number" disabled={!previewEnabled}
        value={formatNumberValue(formik.values.recastPreviewIntervalMs)}
        onChange={handleIntegerChange("recastPreviewIntervalMs")} onBlur={formik.handleBlur}
        error={Boolean(getError("recastPreviewIntervalMs"))}
        helperText={getError("recastPreviewIntervalMs") || "Recast-only: requested delay after each capture. Zero uses 5000 ms. Keyframes and available workers can extend the actual interval."}
        inputProps={{ min: 0, step: 100 }} sx={{ maxWidth: 560 }} />
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        <Tooltip title="Adaptive previews: maximum concurrent decoder sessions, including startup and cleanup. More workers can refresh waiting channels faster, within the RAM budget.">
          <TextField
            label="Preview workers"
            name="previewWorkers"
            type="number"
            size="small"
            disabled={!previewEnabled}
            value={formatNumberValue(formik.values.previewWorkers)}
            onChange={handleIntegerChange("previewWorkers")}
            onBlur={formik.handleBlur}
            error={Boolean(getError("previewWorkers"))}
            helperText={getError("previewWorkers") || " "}
            inputProps={{ min: 1, step: 1 }}
            sx={{ width: fieldWidth }}
          />
        </Tooltip>
        <Tooltip title="Requested preview frames per second for playback modes. Recast-only previews use their separate refresh interval.">
          <TextField
            label="Preview framerate"
            name="previewFramerate"
            type="number"
            size="small"
            disabled={!previewEnabled}
            value={formatNumberValue(formik.values.previewFramerate)}
            onChange={handleFloatChange("previewFramerate")}
            onBlur={formik.handleBlur}
            error={Boolean(getError("previewFramerate"))}
            helperText={getError("previewFramerate") || " "}
            inputProps={{ min: 0.1, step: 0.1 }}
            sx={{ width: fieldWidth }}
          />
        </Tooltip>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        <Tooltip title="Preview width in pixels">
          <TextField
            label="Preview width"
            name="previewWidth"
            type="number"
            size="small"
            disabled={!previewEnabled}
            value={formatNumberValue(formik.values.previewWidth)}
            onChange={handleIntegerChange("previewWidth")}
            onBlur={formik.handleBlur}
            error={Boolean(getError("previewWidth"))}
            helperText={getError("previewWidth") || " "}
            inputProps={{ min: 64, step: 1 }}
            sx={{ width: fieldWidth }}
          />
        </Tooltip>
        <Tooltip title="Preview height in pixels">
          <TextField
            label="Preview height"
            name="previewHeight"
            type="number"
            size="small"
            disabled={!previewEnabled}
            value={formatNumberValue(formik.values.previewHeight)}
            onChange={handleIntegerChange("previewHeight")}
            onBlur={formik.handleBlur}
            error={Boolean(getError("previewHeight"))}
            helperText={getError("previewHeight") || " "}
            inputProps={{ min: 64, step: 1 }}
            sx={{ width: fieldWidth }}
          />
        </Tooltip>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        <Tooltip title="How long preview capture waits for a frame before marking a miss; increase for slower streams.">
          <TextField
            label="Capture timeout (ms)"
            name="previewCaptureTimeoutMs"
            type="number"
            size="small"
            disabled={!previewEnabled}
            value={formatNumberValue(formik.values.previewCaptureTimeoutMs)}
            onChange={handleIntegerChange("previewCaptureTimeoutMs")}
            onBlur={formik.handleBlur}
            error={Boolean(getError("previewCaptureTimeoutMs"))}
            helperText={getError("previewCaptureTimeoutMs") || " "}
            inputProps={{ min: 100, step: 100 }}
            sx={{ width: 220 }}
          />
        </Tooltip>
        <Tooltip title="Ignore initial capture misses for this long after creating a preview target to avoid premature resets.">
          <TextField
            label="Warm-up grace (ms)"
            name="previewTargetWarmupGraceMs"
            type="number"
            size="small"
            disabled={!previewEnabled}
            value={formatNumberValue(formik.values.previewTargetWarmupGraceMs)}
            onChange={handleIntegerChange("previewTargetWarmupGraceMs")}
            onBlur={formik.handleBlur}
            error={Boolean(getError("previewTargetWarmupGraceMs"))}
            helperText={getError("previewTargetWarmupGraceMs") || " "}
            inputProps={{ min: 0, step: 100 }}
            sx={{ width: 220 }}
          />
        </Tooltip>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        <Tooltip title="Minimum delay before retrying preview capture after a miss; higher values reduce retry pressure.">
          <TextField
            label="Backoff min (ms)"
            name="previewBackoffMinMs"
            type="number"
            size="small"
            disabled={!previewEnabled}
            value={formatNumberValue(formik.values.previewBackoffMinMs)}
            onChange={handleIntegerChange("previewBackoffMinMs")}
            onBlur={formik.handleBlur}
            error={Boolean(getError("previewBackoffMinMs"))}
            helperText={getError("previewBackoffMinMs") || " "}
            inputProps={{ min: 100, step: 100 }}
            sx={{ width: 220 }}
          />
        </Tooltip>
        <Tooltip title="Maximum delay cap for miss retries; set higher to better protect the system during prolonged outages.">
          <TextField
            label="Backoff max (ms)"
            name="previewBackoffMaxMs"
            type="number"
            size="small"
            disabled={!previewEnabled}
            value={formatNumberValue(formik.values.previewBackoffMaxMs)}
            onChange={handleIntegerChange("previewBackoffMaxMs")}
            onBlur={formik.handleBlur}
            error={Boolean(getError("previewBackoffMaxMs"))}
            helperText={getError("previewBackoffMaxMs") || " "}
            inputProps={{ min: 100, step: 100 }}
            sx={{ width: 220 }}
          />
        </Tooltip>
      </Box>
    </Box>
  );
};

export default PreviewCfgForm;
